/**
 * AlumniPortal — restricted self-service view for offboarded employees.
 * Route: /app/alumni (only shown when the logged-in user has an alumni_record).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GraduationCap, Download, FileText, Receipt, Ban } from "lucide-react";

const KIND_LABEL = { appointment: "Appointment letter", relieving: "Relieving letter", experience: "Experience certificate" };

export default function AlumniPortal() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/alumni").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const download = async (letterId) => {
    try {
      const r = await api.get(`/employee-letters/${letterId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `letter-${letterId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) { toast.error("Download failed — access may be revoked"); }
  };

  if (data === null) {
    return (
      <div className="p-8 max-w-md mx-auto text-center" data-testid="alumni-none">
        <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <h1 className="text-xl font-semibold mb-2">No alumni record</h1>
        <p className="text-sm text-muted-foreground">You don&apos;t have an alumni profile in this workspace. If this is a mistake, please contact HR.</p>
      </div>
    );
  }

  if (!data.portal_enabled) {
    return (
      <div className="p-8 max-w-md mx-auto text-center" data-testid="alumni-disabled">
        <Ban className="w-12 h-12 text-amber-600 mx-auto mb-3" />
        <h1 className="text-xl font-semibold mb-2">Portal access is disabled</h1>
        <p className="text-sm text-muted-foreground">Your alumni portal has been paused for this workspace. Contact HR at <b>{data.workspace_name}</b> for assistance.</p>
      </div>
    );
  }

  return (
    <div className="pb-8" data-testid="alumni-portal">
      <PageHeader icon={GraduationCap} title={`Alumni — ${data.workspace_name}`} subtitle={`Once a teammate, always part of the family.`} />
      <div className="px-4 sm:px-6 md:px-10 space-y-6">
        {/* Profile card */}
        <div className="border-2 border-border p-4 bg-muted/10" data-testid="alumni-profile">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Meta label="Name" value={data.record.full_name} />
            <Meta label="Work email" value={data.record.work_email} mono />
            <Meta label="Exit date" value={data.record.exit_date} mono />
          </div>
        </div>

        {/* Letters */}
        {data.allow_letter_download && (
          <div className="border border-border" data-testid="alumni-letters">
            <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center gap-1.5"><FileText className="w-3 h-3" /> Your letters</div>
            {data.letters.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">No letters have been issued yet. Contact HR if you need one.</div>
            ) : (
              data.letters.map((l) => (
                <div key={l.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0" data-testid={`alumni-letter-${l.id}`}>
                  <FileText className="w-4 h-4 text-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{KIND_LABEL[l.kind] || l.kind}</div>
                    <div className="text-[10px] text-muted-foreground">Generated {(l.generated_at || "").slice(0, 10)}</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => download(l.id)} data-testid={`alumni-dl-${l.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Payslips */}
        {data.allow_payslip_download && (
          <div className="border border-border" data-testid="alumni-payslips">
            <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center gap-1.5"><Receipt className="w-3 h-3" /> Your payslips</div>
            {data.payslips.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Payslips will appear here once payroll integration is enabled.</div>
            ) : (
              data.payslips.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0">
                  <Receipt className="w-4 h-4 text-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{p.period}</div>
                    <div className="text-[10px] text-muted-foreground">₹ {p.net_pay?.toLocaleString?.() || p.net_pay}</div>
                  </div>
                  {p.pdf_url && (
                    <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" className="text-primary text-xs hover:underline flex items-center gap-1"><Download className="w-3 h-3" /> PDF</a>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : "font-semibold"} mt-0.5`}>{value || "—"}</div>
    </div>
  );
}
